import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";

const EMPTY = {
  name: "", city: "", address: "", description: "", price_per_night: 2000,
  images: "", amenities: "", offers: "", rating: 4.5, rooms_available: 10, property_type: "Hotel",
};

export default function PartnerHotelsPage() {
  const [hotels, setHotels] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);

  const refresh = () => api.get("/partner/hotels").then(({ data }) => setHotels(data));
  useEffect(() => { refresh(); }, []);

  const openNew = () => { setEditingId(null); setForm(EMPTY); setOpen(true); };

  const openEdit = (h) => {
    setEditingId(h.id);
    setForm({
      ...h,
      images: (h.images || []).join(", "),
      amenities: (h.amenities || []).join(", "),
      offers: (h.offers || []).join(", "),
    });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      price_per_night: Number(form.price_per_night),
      rating: Number(form.rating),
      rooms_available: Number(form.rooms_available),
      images: String(form.images).split(",").map((s) => s.trim()).filter(Boolean),
      amenities: String(form.amenities).split(",").map((s) => s.trim()).filter(Boolean),
      offers: String(form.offers).split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editingId) await api.put(`/hotels/${editingId}`, payload);
      else await api.post("/hotels", payload);
      toast.success(editingId ? "Hotel updated" : "Hotel added");
      setOpen(false); refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this hotel?")) return;
    try { await api.delete(`/hotels/${id}`); toast.success("Deleted"); refresh(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">Hotel Partner</p>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="font-serif text-4xl lg:text-5xl text-midnight">Your Properties</h1>
        <a href="/partner-agreement?type=hotel" target="_blank" rel="noreferrer"
           data-testid="view-hotel-agreement-link"
           className="text-xs px-4 py-2 rounded-full bg-stone hover:bg-gold/10 text-midnight border border-black/10">
          View Partner Agreement
        </a>
      </div>

      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-neutral-500">{hotels.length} propert{hotels.length === 1 ? "y" : "ies"}</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="bg-midnight hover:bg-midnight-hover text-bone" data-testid="new-hotel-btn">
              <Plus size={16} className="mr-2" /> Add Property
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-serif text-2xl">{editingId ? "Edit" : "Add"} Hotel</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
              <div><Label>Property Type</Label><Input value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} /></div>
              <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div className="col-span-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div><Label>Price / night (₹)</Label><Input type="number" value={form.price_per_night} onChange={(e) => setForm({ ...form, price_per_night: e.target.value })} /></div>
              <div><Label>Rooms Available</Label><Input type="number" value={form.rooms_available} onChange={(e) => setForm({ ...form, rooms_available: e.target.value })} /></div>
              <div className="col-span-2"><Label>Image URLs (comma-separated)</Label><Textarea value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} /></div>
              <div className="col-span-2"><Label>Amenities (comma-separated)</Label><Input value={form.amenities} onChange={(e) => setForm({ ...form, amenities: e.target.value })} /></div>
              <div className="col-span-2"><Label>Offers (comma-separated)</Label><Input value={form.offers} onChange={(e) => setForm({ ...form, offers: e.target.value })} placeholder="20% OFF, Free breakfast" /></div>
            </div>
            <DialogFooter><Button onClick={save} className="bg-midnight hover:bg-midnight-hover text-bone">{editingId ? "Update" : "Create"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone">
            <tr className="text-left">
              <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Name</th>
              <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">City</th>
              <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Price</th>
              <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Rating</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {hotels.length === 0 ? (
              <tr><td colSpan={5} className="p-12 text-center text-neutral-500">
                No properties yet. Click <strong>Add Property</strong> to list your first hotel.
              </td></tr>
            ) : hotels.map((h) => (
              <tr key={h.id} className="border-t border-black/5">
                <td className="p-4 font-medium text-midnight">{h.name}</td>
                <td className="p-4 text-neutral-600">{h.city}</td>
                <td className="p-4">₹{h.price_per_night.toLocaleString("en-IN")}</td>
                <td className="p-4">{h.rating} ★</td>
                <td className="p-4 flex gap-2 justify-end">
                  <button onClick={() => openEdit(h)} className="p-2 hover:bg-stone rounded-lg"><PencilSimple size={16} /></button>
                  <button onClick={() => del(h.id)} className="p-2 hover:bg-red-50 rounded-lg text-red-700"><Trash size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
